import { Injectable, Optional, TemplateRef } from '@angular/core';

import { MatAutocomplete } from '@angular/material/autocomplete';
import { pairBehaviorSubjects } from 'core/rx-js';
import { BehaviorSubject, distinctUntilChanged, filter, fromEvent, interval, map, merge, startWith, switchMap, take, takeUntil } from 'rxjs';
import { DynamicFormService } from '../../dynamic-form.service';
import { DataSourceControlService } from '../data-source-utils/data-source-control.service';
import { FormControlService } from '../form-control.service';
import { FormControlServiceBase } from '../types/form-control-service.base';
import { IDisposable } from '../types/i-disposable';
import { FormControlUtils } from '../utils/form-control.utils';
import { IAutocompleteFormControlOptions } from './types/i-autocomplete-form-control.options';

@Injectable()
export class AutocompleteFormControlService extends DataSourceControlService implements FormControlServiceBase<'autocomplete'> {

  readonly listItemsSubject = new BehaviorSubject<IAutocompleteFormControlOptions['listItems']>(undefined);
  readonly formControlSubject = this._formControlService?.formControlSubject;
  readonly initializedSubject = this._formControlService?.initializedSubject;
  readonly hintWithSubject = new BehaviorSubject<string>(undefined);
  readonly allowCustomValuesSubject = new BehaviorSubject<boolean>(false);
  readonly disposables: IDisposable[] = [];

  readonly matOptionTemplateRefSubject = new BehaviorSubject<TemplateRef<any>>(undefined);
  readonly autocompleteSubject = new BehaviorSubject<MatAutocomplete>(null)

  readonly scrollEndReachedObs = this.autocompleteSubject.pipe(
    filter(Boolean),
    switchMap((autocomplete) => merge(
      autocomplete.opened.pipe(map(() => true)),
      autocomplete.closed.pipe(map(() => false))
    )
      .pipe(
        startWith(autocomplete.isOpen),
        filter(Boolean),
        switchMap(() => interval(100).pipe(
          map(() => autocomplete.panel.nativeElement as HTMLElement),
          filter(Boolean),
          take(1)
        ))
      )
    ),
    distinctUntilChanged(),
    filter(Boolean),
    switchMap((panelElement) => fromEvent(panelElement, 'scroll').pipe(
      filter(() => panelElement.scrollHeight !== panelElement.clientHeight),
      map(() => (panelElement.scrollTop + panelElement.clientHeight) > (panelElement.scrollHeight - 50)))
    ),
    distinctUntilChanged()
  )


  constructor(
    @Optional() private readonly _formControlService?: FormControlService,
    @Optional() readonly _dynamicFormService?: DynamicFormService
  ) {

    super();

    pairBehaviorSubjects(this.valueSubject, this._formControlService.valueSubject);

    this.formControlSubject?.pipe(
      filter(Boolean),
      take(1),
      takeUntil(this._destroy)
    )
      .subscribe((formControl) => {

        const optionsInstance = formControl.options;
        const disposables = FormControlUtils.mapControlOptionsToService(optionsInstance, this)

        if (!disposables) {
          return;
        }

        this.disposables.push(...disposables)
      })


    this.scrollEndReachedObs.pipe(
      filter(Boolean),
      takeUntil(this._destroy)
    )
      .subscribe(() => {

        const nextPage = this.currentPageSubject.value + 1;
        this.currentPageSubject.next(nextPage)

      })

  }

  ngOnDestory() {

    this.disposables.forEach((disposable) => disposable.dispose());
    this._destroy.next();
    this._destroy.complete();
    super.dispose();

  }

}
